import React, { useState, useEffect, useMemo } from 'react';
// Added Category to imports
import type { Product, Category } from '../types';
import { generateDescription } from '../services/geminiService';
import { SparklesIcon, MapPinIcon, LocationArrowIcon, ClockIcon, GavelIcon } from './Icons';
import { useAppStore } from '../store/useAppStore';
import imageCompression from 'browser-image-compression';
import { storageService } from '../services/storage';
import { getUserCurrentLocation, fuzzLocation } from '../services/geo';

interface AddProductFormProps {
  initialData?: Omit<Product, 'id'> | null;
  mode?: 'create' | 'edit';
  onSave: (product: Omit<Product, 'id'>, imageBlob?: Blob) => Promise<void>;
  onCancel: () => void;
}

const AddProductForm: React.FC<AddProductFormProps> = ({ initialData, mode = 'create', onSave, onCancel }) => {
  const { seller } = useAppStore();
  const [name, setName] = useState('');
  // Fix: Explicitly type the category state to Category
  const [category, setCategory] = useState<Category>('Vegetables');
  const [price, setPrice] = useState('');
  const [unit, setUnit] = useState('kg');
  const [description, setDescription] = useState('');
  const [latitude, setLatitude] = useState<number | undefined>(undefined);
  const [longitude, setLongitude] = useState<number | undefined>(undefined);
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState('');
  
  // Auction fields
  const [isAuction, setIsAuction] = useState(false);
  const [auctionDuration, setAuctionDuration] = useState('24'); // hours
  const [reservePrice, setReservePrice] = useState('');

  const [isGenerating, setIsGenerating] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [error, setError] = useState('');

  const previewUrl = useMemo(() => {
      if (imageBlob) return URL.createObjectURL(imageBlob);
      return existingImageUrl;
  }, [imageBlob, existingImageUrl]);

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    const loadInitialData = async () => {
        if (initialData) {
            setName(initialData.name);
            setCategory(initialData.category);
            setPrice(String(initialData.price));
            setUnit(initialData.unit);
            setDescription(initialData.description);
            setLatitude(initialData.latitude);
            setLongitude(initialData.longitude);
            setExistingImageUrl(initialData.imageUrl);
            setIsAuction(!!initialData.isAuction);
            if (initialData.reservePrice) setReservePrice(String(initialData.reservePrice));
        }
    };
    loadInitialData();
  }, [initialData]);

  if (!seller) {
    return <div className="text-center p-8 text-app-text-accent font-bold">Error: You must be registered to list products.</div>;
  }

  const handleGetLocation = async () => {
    setIsLocating(true);
    setError('');
    try {
        const pos = await getUserCurrentLocation();
        setLatitude(fuzzLocation(pos.coords.latitude));
        setLongitude(fuzzLocation(pos.coords.longitude));
    } catch (err: any) {
        console.error("Location error:", err);
        setError("Could not get location. Please ensure GPS is on and permissions granted.");
    } finally {
        setIsLocating(false);
    }
  };

  const handleGenerateDescription = async () => {
    if (!name) {
      alert('Please enter a product name first.');
      return;
    }
    setIsGenerating(true);
    setError('');
    try {
      const generatedDesc = await generateDescription(name);
      setDescription(generatedDesc);
    } catch (err) {
      setError('Failed to generate description. Please try again.');
      console.error(err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
        alert("Please select a valid image file.");
        return;
    }

    setIsCompressing(true);
    setError('');

    try {
        const options = {
            maxSizeMB: 0.2,
            maxWidthOrHeight: 1200,
            useWebWorker: true,
        };

        const compressedFile = await imageCompression(file, options);
        setImageBlob(compressedFile);
        setIsCompressing(false);
    } catch (err) {
        console.error("Image processing failed:", err);
        setError("Failed to process image. Please try a different one.");
        setIsCompressing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !price) {
        alert("Please fill in all required fields.");
        return;
    }
    
    setIsSubmitting(true);
    try {
        const auctionEndTime = isAuction ? Date.now() + (parseInt(auctionDuration) * 3600000) : undefined;
        
        await onSave({
            name,
            category,
            price: parseFloat(price),
            unit,
            description,
            imageUrl: existingImageUrl || `https://picsum.photos/seed/${name.replace(/\s+/g, '-')}/400/300`,
            seller: seller,
            isActive: initialData ? initialData.isActive : true,
            updatedAt: Date.now(),
            latitude,
            longitude,
            isAuction,
            auctionEndTime,
            reservePrice: reservePrice ? parseFloat(reservePrice) : undefined
        }, imageBlob || undefined);
    } catch (err) {
        console.error("Failed to save product", err);
        alert("Failed to save product. Please try again.");
    } finally {
        setIsSubmitting(false);
    }
  };
  
  const inputClass = "w-full px-3 py-2 bg-input-bg text-input-text border border-input-border rounded-md hover:border-input-border-hover focus:outline-none focus:border-brand-green focus:ring-2 focus:ring-focus-ring";
  const labelClass = "block text-sm font-medium text-app-text mb-1";
  const isEditMode = mode === 'edit';

  return (
    <div className="bg-card-bg p-6 rounded-lg shadow-lg max-w-lg mx-auto border border-card-border">
      <div className="mb-6 border-b border-divider pb-4">
        <h2 className="text-2xl font-bold text-card-title">{isEditMode ? 'Edit Product' : 'List a New Product'}</h2>
        <p className="text-sm text-app-text-secondary mt-1">Listing as <span className="font-semibold text-brand-brown">{seller.name}</span></p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Toggle Auction */}
        <div className="bg-brand-yellow-50 p-4 rounded-xl border border-brand-yellow-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${isAuction ? 'bg-brand-yellow text-btn-accent-text' : 'bg-btn-disabled-bg text-app-text-secondary'}`}>
                    <GavelIcon className="w-5 h-5" />
                </div>
                <div>
                    <h3 className="font-bold text-brand-brown text-sm">List as Auction</h3>
                    <p className="text-[10px] text-app-text-secondary uppercase font-black">Let buyers compete for best price</p>
                </div>
            </div>
            <button 
                type="button" 
                role="switch"
                aria-checked={isAuction}
                aria-label="List as auction"
                onClick={() => setIsAuction(!isAuction)}
                className={`w-12 h-6 rounded-full relative transition-colors ${isAuction ? 'bg-brand-green' : 'bg-input-border'}`}
            >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${isAuction ? 'left-7' : 'left-1'}`} />
            </button>
        </div>

        <div>
          <label htmlFor="name" className={labelClass}>Product Name*</label>
          <input type="text" id="name" value={name} onChange={e => setName(e.target.value)} className={inputClass} required />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="price" className={labelClass}>
                {isAuction ? 'Starting Price*' : 'Fixed Price*'}
            </label>
            <input type="number" id="price" value={price} onChange={e => setPrice(e.target.value)} className={inputClass} required step="0.01" />
          </div>
          <div>
            <label htmlFor="unit" className={labelClass}>Unit</label>
            <input type="text" id="unit" value={unit} onChange={e => setUnit(e.target.value)} className={inputClass} />
          </div>
        </div>

        {isAuction && (
            <div className="grid grid-cols-2 gap-4 p-4 bg-brand-brown-50 rounded-lg animate-in fade-in slide-in-from-top-2">
                <div>
                    <label htmlFor="duration" className="text-xs font-bold text-app-text-secondary uppercase mb-1 flex items-center gap-1">
                        <ClockIcon className="w-3 h-3" />
                        Duration
                    </label>
                    <select 
                        id="duration"
                        value={auctionDuration} 
                        onChange={e => setAuctionDuration(e.target.value)}
                        className={inputClass}
                    >
                        <option value="1">1 Hour</option>
                        <option value="3">3 Hours</option>
                        <option value="6">6 Hours</option>
                        <option value="12">12 Hours</option>
                        <option value="24">24 Hours</option>
                        <option value="48">48 Hours</option>
                    </select>
                </div>
                <div>
                    <label htmlFor="reserve" className="block text-xs font-bold text-app-text-secondary uppercase mb-1">
                        Reserve Price
                    </label>
                    <input 
                        id="reserve"
                        type="number" 
                        value={reservePrice} 
                        onChange={e => setReservePrice(e.target.value)}
                        className={inputClass}
                        placeholder="Optional"
                        step="0.01"
                    />
                </div>
            </div>
        )}

        <div>
            <label htmlFor="category" className={labelClass}>Category</label>
            {/* Fix: Cast the select value to Category to match state type */}
            <select id="category" value={category} onChange={e => setCategory(e.target.value as Category)} className={inputClass}>
                <option>Vegetables</option>
                <option>Fruits</option>
                <option>Dairy & Eggs</option>
                <option>Bakery</option>
                <option>Pantry</option>
                <option>Meats</option>
            </select>
        </div>

        {/* Geolocation Section */}
        <div className="bg-brand-green-50 p-4 rounded-lg border border-brand-green-100 space-y-3">
            <div className="flex justify-between items-center">
                <span className="text-sm font-bold text-brand-brown flex items-center gap-1">
                    <MapPinIcon className="w-4 h-4" />
                    Product Location
                </span>
                <button 
                    type="button" 
                    onClick={handleGetLocation} 
                    disabled={isLocating}
                    className="text-xs bg-btn-primary text-btn-primary-text px-3 py-1.5 rounded-full font-bold hover:bg-btn-primary-hover disabled:opacity-50 transition-colors flex items-center gap-1"
                >
                    <LocationArrowIcon className={`w-3 h-3 ${isLocating ? 'animate-pulse' : ''}`} />
                    {isLocating ? 'Locating...' : 'Use Current'}
                </button>
            </div>
            {latitude && longitude ? (
                <p className="text-xs text-brand-green font-medium">
                    📍 Location set (approx. {latitude}, {longitude})
                    <button type="button" onClick={() => {setLatitude(undefined); setLongitude(undefined);}} className="ml-2 text-error-text underline">Clear</button>
                </p>
            ) : (
                <p className="text-xs text-app-text-secondary italic">Optional: Adding a location helps nearby buyers find you.</p>
            )}
        </div>

        <div>
            <label htmlFor="image" className={labelClass}>Product Image</label>
            <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-24 h-24 bg-brand-brown-50 rounded-lg overflow-hidden border border-card-border relative shadow-inner">
                    {isCompressing ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-brand-brown-50 z-10">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-green"></div>
                        </div>
                    ) : previewUrl ? (
                        <img 
                          src={previewUrl.startsWith('storage://') ? 'https://picsum.photos/200/200?blur=10' : previewUrl} 
                          alt="Preview" 
                          className="w-full h-full object-cover" 
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-app-text-secondary text-xs">No Image</div>
                    )}
                </div>
                <div className="flex-grow">
                    <input 
                        id="image"
                        type="file" 
                        accept="image/*" 
                        onChange={handleImageChange} 
                        disabled={isCompressing}
                        className="block w-full text-sm text-app-text-secondary file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-btn-primary file:text-btn-primary-text hover:file:bg-btn-primary-hover disabled:opacity-50 disabled:cursor-wait"
                    />
                    <p className="text-[10px] text-app-text-secondary mt-2 font-medium uppercase tracking-tight">
                        {isCompressing ? 'Optimizing image for storage...' : 'Images are converted to binary objects for efficiency.'}
                    </p>
                </div>
            </div>
        </div>

        <div>
          <label htmlFor="description" className={labelClass}>Description</label>
          <textarea id="description" value={description} onChange={e => setDescription(e.target.value)} className={inputClass} rows={3}></textarea>
          <button type="button" onClick={handleGenerateDescription} disabled={isGenerating || isCompressing} className="mt-2 flex items-center gap-2 text-sm text-brand-green font-bold hover:text-brand-green-hover disabled:opacity-50 disabled:cursor-not-allowed">
            <SparklesIcon className="w-5 h-5"/>
            {isGenerating ? 'Analyzing Product...' : 'AI Enhanced Description'}
          </button>
          {error && <p role="alert" className="text-error-text text-sm mt-1">{error}</p>}
        </div>

        <div className="flex justify-end gap-4 pt-4 border-t border-divider mt-6">
            <button type="button" onClick={onCancel} disabled={isSubmitting} className="py-2 px-4 bg-btn-secondary-bg text-btn-secondary-text border border-btn-secondary-border rounded-md hover:bg-btn-secondary-hover font-bold transition-colors">Cancel</button>
            <button 
                type="submit" 
                disabled={isSubmitting || isCompressing} 
                className="py-2 px-6 bg-btn-primary text-btn-primary-text font-black rounded-md hover:bg-btn-primary-hover disabled:opacity-50 disabled:cursor-not-allowed shadow-card-hover transition-all active:scale-95"
            >
                {isSubmitting ? 'Saving...' : isCompressing ? 'Processing...' : (isEditMode ? 'Update Product' : 'List Product')}
            </button>
        </div>
      </form>
    </div>
  );
};

export default AddProductForm;
